
import React, { useMemo, useRef, useState, useCallback } from 'react';
// Fix: Use named imports for ReactFlow components and types.
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  Connection,
  Edge,
  Node,
  OnConnectStartParams,
  NodeChange,
  EdgeChange,
  useReactFlow,
} from 'reactflow';
import { Columns3 } from 'lucide-react';

import { AppNode, SimulationState, WorkflowNodeData, ProcessDefinition, Swimlane } from '../../types';
import Sidebar from '../organisms/Sidebar';
import PropertiesPanel from '../organisms/PropertiesPanel';
import TopBar from '../organisms/TopBar';
import BottomPanel from '../organisms/BottomPanel';
import NodeModal from '../organisms/NodeModal';
import { DeploymentModal } from '../organisms/DeploymentModal';
import CustomNode from '../molecules/CustomNode';
import DeletableEdge from '../molecules/DeletableEdge';
import Swimlanes from '../molecules/Swimlanes';
import PaneContextMenu from '../molecules/PaneContextMenu';
import { useEditorConfig } from '../../contexts/EditorContext';
import { LoadingOverlay } from '../molecules/LoadingOverlay';
import { VariableDefinition } from '../../utils/variableExtraction';
import { CanvasSearch } from '../molecules/CanvasSearch';
import { useSearch } from '../../hooks/useSearch';
import { useTheme } from '../../contexts/ThemeContext';

export interface WorkflowEditorProps {
    nodes: AppNode[];
    edges: Edge[];
    onNodesChange: (changes: NodeChange[]) => void;
    onEdgesChange: (changes: EdgeChange[]) => void;
    onConnect: (connection: Connection) => void;
    onConnectStart: (_: any, params: OnConnectStartParams) => void;
    onConnectEnd: (event: any) => void;
    onDrop: (event: React.DragEvent) => void;
    onDragOver: (event: React.DragEvent) => void;
    onDragLeave?: () => void;
    
    simulationState: SimulationState;
    onToggleSimulation: () => void;
    onResetSimulation: () => void;

    activeProcessId: string;
    processes: ProcessDefinition[];
    onSelectProcess: (id: string) => void;
    onCreateProcess: () => void;
    onCreateSubProcess: () => void;
    onDeleteProcess: (id: string) => void;
    onUpdateProcess: (id: string, data: Partial<ProcessDefinition>) => void;
    onSaveProject: (opts?: { bypassValidation?: boolean }) => void;
    onLoadProject: (file: File) => void;
    
    onSave?: () => void;
    onSaveAndQuit?: () => void;
    onBack?: () => void;
    
    onAutoLayout: () => void;

    onNodeClick: (e: React.MouseEvent, node: Node) => void;
    onEdgeClick: (e: React.MouseEvent, edge: Edge) => void;
    onNodeDoubleClick: (e: React.MouseEvent, node: Node) => void;
    onNodeContextMenu: (e: React.MouseEvent, node: Node) => void;
    onNodeDragStop: (e: React.MouseEvent, node: Node) => void;
    onPaneClick: () => void;
    swimlanes: Swimlane[];
    selectedSwimlaneId: string | null;
    selectSwimlane: (id: string | null) => void;
    addSwimlane: (position?: { x: number; y: number }) => void;
    renameSwimlane: (id: string, name: string) => void;
    moveSwimlane: (id: string, x: number, y: number) => void;
    moveSwimlaneBy: (id: string, dx: number, dy: number) => void;
    resizeSwimlane: (id: string, width: number, height: number) => void;
    setSwimlaneColor: (id: string, color: string) => void;
    removeSwimlane: (id: string) => void;
    onSelectionChange?: (params: { nodes: Node[]; edges: Edge[] }) => void;
    selectedNode: AppNode | null;
    selectedEdge: Edge | null;
    updateNodeData: (id: string, data: Partial<WorkflowNodeData>) => void;
    updateEdgeData: (id: string, data: Partial<Edge>) => void;
    onRenameNode: (oldId: string, newId: string) => void;
    
    variableContext?: VariableDefinition[];

    isModalOpen: boolean;
    setIsModalOpen: (open: boolean) => void;
    activeTabId: string;
    setActiveTabId: (id: string) => void;
    isDeployModalOpen: boolean;
    setIsDeployModalOpen: (open: boolean) => void;
    
    isLoading: boolean;
    autoSaveState?: { isSaving: boolean; lastSaved: Date | null };

    // Validation
    validationIssues?: import('../../utils/validation').ValidationIssue[];
    validationErrors?: number;
    validationWarnings?: number;
    processIssueCounts?: Record<string, { errors: number; warnings: number }>;
    onFocusNode?: (nodeId: string) => void;
}

const edgeTypes = { deletable: DeletableEdge };

const WorkflowEditor: React.FC<WorkflowEditorProps> = (props) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const { nodeTypes: configNodeTypes } = useEditorConfig();
    const { theme } = useTheme();
    const { screenToFlowPosition } = useReactFlow();

    // Pane right-click menu — anchored in viewport coords, carrying the
    // flow-space coords of the click so menu items can act at that point.
    const [paneMenu, setPaneMenu] = useState<
        { screenX: number; screenY: number; flowX: number; flowY: number } | null
    >(null);

    const onPaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
        event.preventDefault();
        const flow = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        setPaneMenu({
            screenX: event.clientX,
            screenY: event.clientY,
            flowX: flow.x,
            flowY: flow.y,
        });
    }, [screenToFlowPosition]);

    const closePaneMenu = useCallback(() => setPaneMenu(null), []);

    const nodeTypes = useMemo(() => {
        const types: Record<string, any> = {};
        Object.keys(configNodeTypes).forEach(key => {
            types[key] = CustomNode;
        });
        return types;
    }, [configNodeTypes]);

    const { query, setQuery, matches, activeIndex, nextMatch } = useSearch({
      nodes: props.nodes,
      onSelectNode: (id) => {
        const node = props.nodes.find(n => n.id === id);
        if (node) props.onNodeClick({} as any, node);
      }
    });

    const activeProcessName = props.processes.find(p => p.id === props.activeProcessId)?.name || 'Project';

    return (
        <div className={`flex flex-col h-full w-full overflow-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans relative ${theme}`}>
          <LoadingOverlay isVisible={props.isLoading} message="Processing..." />
          
          <TopBar 
            simulationState={props.simulationState} 
            onToggleSimulation={props.onToggleSimulation} 
            onReset={props.onResetSimulation}
            onExport={props.onSaveProject}
            onImport={props.onLoadProject}
            onSave={props.onSave}
            onSaveAndQuit={props.onSaveAndQuit}
            onBack={props.onBack}
            processes={props.processes}
            activeProcessId={props.activeProcessId}
            onSelectProcess={props.onSelectProcess}
            onCreateProcess={props.onCreateProcess}
            onCreateSubProcess={props.onCreateSubProcess}
            onDeleteProcess={props.onDeleteProcess}
            currentNodes={props.nodes}
            onAutoLayout={props.onAutoLayout}
            autoSaveState={props.autoSaveState}
            validationErrors={props.validationErrors}
            validationWarnings={props.validationWarnings}
            processIssueCounts={props.processIssueCounts}
          />
          
          <div className="flex flex-1 overflow-hidden relative" onDragLeave={props.onDragLeave}>
            <Sidebar />
            
            <div className="flex-1 flex flex-col h-full relative min-w-0">
                <div className="flex-1 relative h-full bg-slate-100 dark:bg-slate-950/50" ref={reactFlowWrapper}>
                    <CanvasSearch
                      query={query}
                      onQueryChange={setQuery}
                      matchCount={matches.length}
                      activeIndex={activeIndex}
                      onNext={nextMatch}
                      onClear={() => setQuery('')}
                    />

                    <ReactFlow
                        nodes={props.nodes}
                        edges={props.edges}
                        onNodesChange={props.onNodesChange}
                        onEdgesChange={props.onEdgesChange}
                        onConnect={props.onConnect}
                        onConnectStart={props.onConnectStart}
                        onConnectEnd={props.onConnectEnd}
                        onDrop={props.onDrop}
                        onDragOver={props.onDragOver}
                        onNodeClick={props.onNodeClick}
                        onEdgeClick={props.onEdgeClick}
                        onPaneClick={props.onPaneClick}
                        onSelectionChange={props.onSelectionChange}
                        onNodeDoubleClick={props.onNodeDoubleClick}
                        onNodeContextMenu={props.onNodeContextMenu}
                        onPaneContextMenu={onPaneContextMenu}
                        onNodeDragStop={props.onNodeDragStop}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        fitView
                        style={{ width: '100%', height: '100%' }}
                        proOptions={{ hideAttribution: true }}
                        snapToGrid={true}
                        snapGrid={[15, 15]}
                        nodesDraggable={true}
                        nodesConnectable={true}
                        elementsSelectable={true}
                        deleteKeyCode={['Backspace', 'Delete']}
                        defaultEdgeOptions={{
                            type: 'deletable',
                            interactionWidth: 20,
                            style: { strokeWidth: 2, stroke: '#94a3b8' }
                        }}
                    >
                        <Controls className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm !text-slate-600 dark:!text-slate-400 rounded-lg" />
                        <MiniMap
                            nodeStrokeColor={(n) => {
                                if (n.data.status === 'error') return '#ef4444';
                                if (n.data.status === 'active') return '#14b8a6';
                                if (n.data.status === 'completed') return '#10b981';
                                return '#94a3b8';
                            }}
                            zoomable
                            pannable
                            className="!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-800 !shadow-sm !rounded-xl"
                        />
                        <Background
                            color={theme === 'dark' ? '#334155' : '#cbd5e1'}
                            gap={20}
                            size={1}
                            variant={BackgroundVariant.Dots}
                        />
                        <Swimlanes
                            swimlanes={props.swimlanes}
                            selectedSwimlaneId={props.selectedSwimlaneId}
                            onSelect={props.selectSwimlane}
                            onMoveBy={props.moveSwimlaneBy}
                            onResize={props.resizeSwimlane}
                            onRemove={props.removeSwimlane}
                        />
                    </ReactFlow>
                </div>
                
                <BottomPanel
                    simulationState={props.simulationState}
                    issues={props.validationIssues}
                    onFocusNode={props.onFocusNode}
                />
            </div>
    
            <PropertiesPanel
                selectedNode={props.selectedNode}
                selectedEdge={props.selectedEdge}
                selectedSwimlane={props.swimlanes.find(l => l.id === props.selectedSwimlaneId) || null}
                renameSwimlane={props.renameSwimlane}
                moveSwimlane={props.moveSwimlane}
                resizeSwimlane={props.resizeSwimlane}
                setSwimlaneColor={props.setSwimlaneColor}
                removeSwimlane={props.removeSwimlane}
                nodes={props.nodes}
                edges={props.edges}
                activeProcess={props.processes.find(p => p.id === props.activeProcessId)}
                updateNodeData={props.updateNodeData}
                updateEdgeData={props.updateEdgeData}
                updateProcessData={props.onUpdateProcess}
                onOpenModal={() => props.setIsModalOpen(true)}
                variableContext={props.variableContext}
                onRenameNode={props.onRenameNode}
                validationIssues={props.validationIssues}
            />
          </div>
          
          <NodeModal 
            isOpen={props.isModalOpen} 
            onClose={() => props.setIsModalOpen(false)}
            selectedNode={props.selectedNode}
            updateNodeData={props.updateNodeData}
            variableContext={props.variableContext}
            activeTabId={props.activeTabId}
            setActiveTabId={props.setActiveTabId}
          />

          <DeploymentModal
            isOpen={props.isDeployModalOpen}
            onClose={() => props.setIsDeployModalOpen(false)}
            projectName={activeProcessName}
          />

          {paneMenu && (
            <PaneContextMenu
              x={paneMenu.screenX}
              y={paneMenu.screenY}
              onClose={closePaneMenu}
              items={[
                {
                  label: 'Add Swimlane',
                  icon: <Columns3 size={14} className="text-sky-500" />,
                  onClick: () => props.addSwimlane({ x: paneMenu.flowX, y: paneMenu.flowY }),
                },
              ]}
            />
          )}
        </div>
      );
};

export default WorkflowEditor;